import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Check, ImagePlus, RotateCcw, Send, Sparkles, Wrench, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { useYacht } from "../../context/YachtContext";
import { api, tokenStore } from "../../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

const SUGGESTIONS = [
  "What needs my attention right now?",
  "The engine is not starting — help me troubleshoot",
  "Summarize the open tasks",
  "What's low in stock?",
];

const VALID_PRIORITIES = ["low", "medium", "high", "critical"];

// Resize a picked photo to a ≤1200px JPEG data URL so the request stays small.
async function fileToDataUrl(file, maxDim = 1200) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = url;
    });
    const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(img.naturalWidth * scale);
    c.height = Math.round(img.naturalHeight * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Split a trailing `TASK_SUGGESTION: {json}` line off an assistant message.
 *  While streaming, a partial trailing line is hidden until it parses. */
function splitSuggestion(content) {
  const idx = content.indexOf("TASK_SUGGESTION:");
  if (idx === -1) return { text: content, suggestion: null };
  const text = content.slice(0, idx).trimEnd();
  let suggestion = null;
  try {
    const parsed = JSON.parse(content.slice(idx + "TASK_SUGGESTION:".length).trim());
    if (parsed && parsed.title) suggestion = parsed;
  } catch { /* still streaming or malformed — hide the raw line */ }
  return { text, suggestion };
}

/**
 * "Ask Seavanto" — floating AI assistant. Streams answers from
 * POST /api/assistant/chat (SSE), grounded in the active yacht's data
 * filtered by the user's role. One session per user+yacht, persisted in
 * localStorage so the conversation survives reloads; the reset button
 * starts a fresh session.
 */
export default function AssistantChat() {
  const { user } = useAuth();
  const { activeYacht } = useYacht();
  const [open, setOpen] = useState(false);
  // The FAB would cover the reply-send button on the Messages page — hide it there.
  const { pathname } = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [attach, setAttach] = useState(null); // data URL of the photo to send
  const fileRef = useRef(null);
  const bottomRef = useRef(null);

  const storageKey = user && activeYacht ? `seavanto_ai_session_${user.id}_${activeYacht.id}` : null;

  useEffect(() => {
    if (!storageKey) return;
    let sid = localStorage.getItem(storageKey);
    if (!sid) {
      sid = (crypto.randomUUID ? crypto.randomUUID() : `s${Date.now()}`);
      localStorage.setItem(storageKey, sid);
    }
    setSessionId(sid);
    setMessages([]);
  }, [storageKey]);

  useEffect(() => {
    if (!open || !sessionId) return;
    fetch(`${API}/api/assistant/history?session_id=${sessionId}`, {
      headers: { Authorization: `Bearer ${tokenStore.get()}` },
      credentials: "include",
    })
      .then((r) => r.json())
      .then((rows) => setMessages(rows.map((m) => ({ role: m.role, content: m.content, hasImage: !!m.has_image }))))
      .catch(() => {});
  }, [open, sessionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  if (!user || !activeYacht) return null;

  const resetSession = () => {
    const sid = crypto.randomUUID ? crypto.randomUUID() : `s${Date.now()}`;
    localStorage.setItem(storageKey, sid);
    setSessionId(sid);
    setMessages([]);
  };

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setAttach(await fileToDataUrl(file));
    } catch {
      toast.error("Could not read that photo — try another one");
    }
  };

  const send = async (text) => {
    const msg = (text || input).trim();
    const image = text ? null : attach; // suggestion clicks never carry the pending photo
    if ((!msg && !image) || busy || !sessionId) return;
    setInput("");
    setAttach(null);
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg, image }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/api/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tokenStore.get()}` },
        credentials: "include",
        body: JSON.stringify({ yacht_id: activeYacht.id, session_id: sessionId, message: msg, image_base64: image || null }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                role: "assistant",
                content: copy[copy.length - 1].content + ev.delta,
              };
              return copy;
            });
          } else if (ev.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                role: "assistant",
                content: "Sorry — I couldn't answer that right now. Please try again.",
              };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Connection problem — please try again.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const createTask = async (sugg, idx) => {
    try {
      // The nearest preceding photo in this conversation becomes the task's
      // "before" photo (only available for photos sent this session).
      let photoPath = null;
      for (let j = idx - 1; j >= 0; j--) {
        const pm = messages[j];
        if (pm.role !== "user") continue;
        if (pm.image) {
          try {
            const blob = await (await fetch(pm.image)).blob();
            const fd = new FormData();
            fd.append("file", new File([blob], "ai-diagnosis.jpg", { type: "image/jpeg" }));
            fd.append("yacht_id", activeYacht.id);
            fd.append("folder", "maintenance/before");
            const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            photoPath = data.storage_path;
          } catch { /* task is still created without the photo */ }
          break;
        }
        if (pm.content) break; // a text-only question in between → photo isn't about this fault
      }
      const priority = VALID_PRIORITIES.includes(sugg.priority) ? sugg.priority : "medium";
      const { data } = await api.post("/maintenance", {
        yacht_id: activeYacht.id,
        title: sugg.title,
        system_category: sugg.system_category || "",
        system_categories: sugg.system_category ? [sugg.system_category] : [],
        priority,
        description: sugg.description || "",
        before_photos: photoPath ? [photoPath] : [],
      });
      setMessages((m) => m.map((msg, i) => (i === idx ? { ...msg, taskCreatedId: data?.id || "ok" } : msg)));
      toast.success(photoPath
        ? "Maintenance task created with your photo attached — see the Tasks page"
        : "Maintenance task created — see the Tasks page");
    } catch (e) {
      toast.error(e?.response?.status === 403
        ? "You don't have permission to create tasks on this yacht"
        : "Could not create the task — please try again");
    }
  };

  return (
    <>
      {!open && !pathname.startsWith("/app/messages") && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-ink text-white pl-3 pr-4 py-3 shadow-lg hover:bg-black transition-colors"
          data-testid="assistant-open-btn"
          aria-label="Ask Seavanto AI assistant"
        >
          <Sparkles className="h-4 w-4" />
          <span className="text-sm font-medium hidden sm:inline">Ask Seavanto</span>
        </button>
      )}

      {open && (
        <div
          className="fixed z-50 bg-white border border-zinc-200 shadow-2xl flex flex-col inset-0 sm:inset-auto sm:bottom-5 sm:right-5 sm:h-[600px] sm:max-h-[calc(100vh-40px)] sm:w-[400px] sm:rounded-xl"
          data-testid="assistant-panel"
        >
          <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-100">
            <span className="grid place-items-center h-8 w-8 rounded-full bg-ink text-white">
              <Sparkles className="h-4 w-4" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium leading-tight">Ask Seavanto</div>
              <div className="text-[11px] text-zinc-500 truncate">{activeYacht.name} · AI assistant</div>
            </div>
            <button type="button" onClick={resetSession} title="New conversation"
              className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded" data-testid="assistant-reset-btn">
              <RotateCcw className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setOpen(false)}
              className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded" data-testid="assistant-close-btn">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="assistant-messages">
            {messages.length === 0 && (
              <div className="pt-4">
                <div className="text-sm text-zinc-600">
                  Hi {user.name?.split(" ")[0] || ""} 👋 Ask me anything about{" "}
                  <span className="font-medium">{activeYacht.name}</span> — tasks, stock,
                  logbook, petty cash — or describe a technical problem and I&apos;ll help you
                  troubleshoot it like a marine engineer.
                </div>
                <div className="mt-3 flex flex-col gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} type="button" onClick={() => send(s)}
                      className="text-left text-[13px] border border-zinc-200 rounded-lg px-3 py-2 hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
                      data-testid="assistant-suggestion">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => {
              const { text, suggestion } = m.role === "assistant" ? splitSuggestion(m.content) : { text: m.content, suggestion: null };
              return (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex flex-col items-start gap-1.5"}>
                  <div
                    className={
                      m.role === "user"
                        ? "max-w-[85%] rounded-2xl rounded-br-sm bg-ink text-white px-3.5 py-2 text-[13px] whitespace-pre-wrap"
                        : "max-w-[85%] rounded-2xl rounded-bl-sm bg-zinc-100 text-zinc-900 px-3.5 py-2 text-[13px] whitespace-pre-wrap"
                    }
                    data-testid={`assistant-msg-${m.role}`}
                  >
                    {m.role === "user" && m.image && (
                      <img src={m.image} alt="" className="rounded-lg mb-1.5 max-h-40 w-auto" data-testid="assistant-msg-image" />
                    )}
                    {m.role === "user" && !m.image && m.hasImage && (
                      <div className="text-[11px] opacity-75 mb-0.5">📷 Photo attached</div>
                    )}
                    {text || (busy && i === messages.length - 1 ? "…" : "")}
                  </div>
                  {suggestion && (
                    <div className="max-w-[85%] w-full border border-zinc-200 rounded-xl p-3 bg-white shadow-sm" data-testid="assistant-task-suggestion">
                      <div className="flex items-start gap-2">
                        <span className="grid place-items-center h-7 w-7 rounded-lg bg-zinc-100 shrink-0">
                          <Wrench className="h-3.5 w-3.5 text-zinc-600" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-medium leading-snug">{suggestion.title}</div>
                          <div className="text-[11px] text-zinc-500 mt-0.5">
                            {[suggestion.system_category, suggestion.priority].filter(Boolean).join(" · ")}
                          </div>
                        </div>
                      </div>
                      {m.taskCreatedId ? (
                        <div className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-emerald-700" data-testid="assistant-task-created">
                          <Check className="h-3.5 w-3.5" /> Task created — find it on the Tasks page
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => createTask(suggestion, i)}
                          className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-ink text-white text-[12px] px-3 py-1.5 hover:bg-black transition-colors"
                          data-testid="assistant-create-task-btn"
                        >
                          <Wrench className="h-3.5 w-3.5" /> Create maintenance task
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {attach && (
            <div className="px-3 pt-2 flex items-center gap-2" data-testid="assistant-attach-preview">
              <img src={attach} alt="" className="h-12 w-12 rounded-md object-cover border border-zinc-200" />
              <span className="text-[12px] text-zinc-500 flex-1">Photo attached</span>
              <button type="button" onClick={() => setAttach(null)}
                className="p-1 text-zinc-400 hover:text-zinc-700 rounded" data-testid="assistant-attach-remove" aria-label="Remove photo">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          <form
            className="border-t border-zinc-100 p-3 flex items-center gap-2"
            onSubmit={(e) => { e.preventDefault(); send(); }}
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={pickPhoto}
              data-testid="assistant-photo-input"
            />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
              className="grid place-items-center h-9 w-9 rounded-lg border border-zinc-200 text-zinc-500 hover:text-zinc-800 hover:border-zinc-400 disabled:opacity-40 transition-colors"
              data-testid="assistant-photo-btn" aria-label="Attach a photo" title="Attach a photo">
              <ImagePlus className="h-4 w-4" />
            </button>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={attach ? "Describe the problem (optional)…" : "Ask about your yacht…"}
              className="flex-1 text-sm border border-zinc-200 rounded-lg px-3 py-2 outline-none focus:border-zinc-400"
              data-testid="assistant-input"
            />
            <button type="submit" disabled={busy || (!input.trim() && !attach)}
              className="grid place-items-center h-9 w-9 rounded-lg bg-ink text-white disabled:opacity-40 hover:bg-black transition-colors"
              data-testid="assistant-send-btn" aria-label="Send">
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
